<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { IconSearch } from '@/components/svg'
import { useDeviceDetect } from '@/composables/useDeviceDetect'
import { useRecentSearches } from '@/composables/useRecentSearches'

const { isMobile } = useDeviceDetect()

const props = defineProps({
  searchKeywords: { 
    type: String, 
    default: '' 
  },
  placeholder: { 
    type: String, 
    default: '' 
  },
  recent: { 
    type: Object, 
    required: true 
  },
  recentKey: { 
    type: [String, Object], 
    default: '__default__' 
  },
  // 임의 ================
  recentMax: { 
    type: Number, 
    default: 10 
  },
  recentTtlHours: { 
    type: Number, 
    default: 480, 
  },
  recentStorage: { 
    type: String, 
    default: 'local' 
  },
  searchOnPick: { 
    type: Boolean, 
    default: true,
  },

})

const emit = defineEmits(['update:searchKeywords', 'search', 'clear'])

const modelValue = ref(props.searchKeywords)
const placeholderText = computed(() => props.placeholder || '제목 혹은 내용 검색')

// 최근 검색어 composable
const recent = props.recent;
const recentItems = recent.items;

// 드롭다운 표시 제어
const rootEl = ref(null)
const showDropdown = ref(false)

watch(
  () => props.searchKeywords,
  (v) => (modelValue.value = v)
)

watch(modelValue, (v) => emit('update:searchKeywords', v))

const onSearch = () => {
  emit('search', modelValue.value)
  showDropdown.value = false
}

function onClear() {
  modelValue.value = ''
  emit('update:searchKeywords', '')
  emit('clear')
  showDropdown.value = false
}

function onFocus() {
  showDropdown.value = true
}

function toggleSearchList() {
  showDropdown.value = !showDropdown.value
}

// const justPicked = ref(false)
// let pickTimer = null
function onPick(item) {
  modelValue.value = item
  if (props.searchOnPick) {
    // justPicked.value = true
    emit('search', modelValue.value)
    showDropdown.value = false
    // justPicked.value = false
  } else {
    showDropdown.value = false
  }
}

// 개별 삭제
function onRemove(item, e) {
  e.stopPropagation()
  recent.remove(item)
}

// 바깥 클릭 닫기
function handleDocClick(e) {
  if (!rootEl.value) return
  if (!rootEl.value.contains(e.target)) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocClick)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocClick)
})
</script>

<template>
  <div class="search-box" ref="rootEl">
    <VTextField
      class="input"
      clearable
      :variant="isMobile ? 'plain' : 'underlined'"
      :placeholder="placeholderText"
      v-model="modelValue"
      @click="toggleSearchList"
      @keyup.enter="onSearch"
      @click:clear="onClear"
    />
    <span class="btn-box" style="cursor: pointer" @click="onSearch">
      <IconSearch />
    </span>

    <div
      v-if="showDropdown && recentItems.length"
      class="search-recent"
      :class="{mobile: isMobile}"
      role="listbox"
      aria-label="최근 검색어"
    >
      <div
        v-for="(item, idx) in recentItems"
        :key="idx"
        class="search-recent__item"
        role="option"
        @click="onPick(item)"
      >
        <span class="search-recent__text">{{ item }}</span>
        <button
          class="search-recent__remove"
          type="button"
          aria-label="최근 검색어 삭제"
          @click="onRemove(item, $event)"
        >
          ✕
        </button>
      </div>

      <div class="search-recent__footer">
        <button type="button" class="search-recent__clear-all" @click="recent.clear()">
          전체 삭제
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.input {
  border: 0;
}
.btn-box {
  margin-top: 10px;
}
.search-box {
  position: relative;
}

.search-recent {
  position: absolute;
  top: calc(100% + 16px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0,0,0,.08);
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;
  &.mobile {
    top: calc(100% + 6px);
  }
}

.search-recent__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: #f7f7f7;
  }
}

.search-recent__text {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-recent__remove {
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  opacity: .6;

  &:hover {
    opacity: 1;
  }
}
.search-recent__footer {
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  border-top: 1px solid rgba(0,0,0,.06);
  background: #fafafa;
}

.search-recent__clear-all {
  appearance: none;
  border: none;
  background: transparent;
  color: #666;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #333;
    text-decoration: underline;
  }
}
</style>
